import React, { useEffect, useState } from "react";
import Sidebar from "../../../component/includes/sidebar";
import { Pencil, Trash2, Plus, Calendar } from "lucide-react";
import { CreateVisiData, UpdateVisiData, VisiContent, visiService } from "../../../services/Tentang/VisiManagement";

const VisiManagement = () => {
  const [visiList, setVisiList] = useState<VisiContent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [editingVisi, setEditingVisi] = useState<VisiContent | null>(null);
  const [newVisi, setNewVisi] = useState<CreateVisiData>({ description: "" });
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);

  useEffect(() => {
    fetchVisiList();
  }, []);

  const fetchVisiList = async () => {
    setLoading(true);
    try {
      const response = await visiService.getVisiList();
      setVisiList(Array.isArray(response) ? response : []);
    } catch (err: any) {
      setError(err?.error || err?.message || "Failed to fetch visi list");
      setIsErrorModalOpen(true);
      console.error("Error fetching visi:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVisi = async () => {
    if (!newVisi.description.trim()) {
      setError("Visi description cannot be empty");
      setIsErrorModalOpen(true);
      return;
    }

    try {
      await visiService.createVisi(newVisi);
      await fetchVisiList();
      setSuccessMessage("Visi created successfully");
      setIsSuccessModalOpen(true);
      setNewVisi({ description: "" });
      setIsModalOpen(false);
    } catch (err: any) {
      setError(err?.error || err?.message || "Failed to create visi");
      setIsErrorModalOpen(true);
    }
  };

  const handleUpdateVisi = async () => {
    if (!editingVisi) return;

    try {
      const updatedData: UpdateVisiData = {
        id: editingVisi.id,
        description: editingVisi.description,
      };
      await visiService.updateVisi(updatedData);
      await fetchVisiList();
      setSuccessMessage("Visi updated successfully");
      setIsSuccessModalOpen(true);
      setEditingVisi(null);
      setIsModalOpen(false);
    } catch (err: any) {
      setError(err?.error || err?.message || "Failed to update visi");
      setIsErrorModalOpen(true);
    }
  };

  const handleDeleteVisi = async (visiId: number) => {
    if (!window.confirm("Are you sure you want to delete this visi?")) return;

    try {
      await visiService.deleteVisi(visiId);
      await fetchVisiList();
      setSuccessMessage("Visi deleted successfully");
      setIsSuccessModalOpen(true);
    } catch (err: any) {
      setError(err?.error || err?.message || "Failed to delete visi");
      setIsErrorModalOpen(true);
    }
  };

  const truncateDescription = (text: string, maxLength: number = 120) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const filteredVisiList = visiList.filter((v) =>
    v.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalVisi = filteredVisiList.length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar />

      <div className="flex-1 flex flex-col">
<div className="flex-1 p-4 md:p-6 mt-14">
          <div className="max-w-7xl mx-auto">
            {/* Header - Mirip Gambar */}
            <div className="bg-white rounded-3xl shadow-sm p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center">
                  <span className="text-white text-3xl">📋</span>
                </div>
                <div>
                  <h1 className="text-3xl font-semibold text-gray-900">Visi Management</h1>
                  <p className="text-gray-500 text-sm mt-1">Manage your company visi content</p>
                </div>
              </div>

              <button
                onClick={() => {
                  setIsModalOpen(true);
                  setEditingVisi(null);
                  setNewVisi({ description: "" });
                }}
                className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-2xl font-medium transition-all active:scale-95"
              >
                <Plus className="w-5 h-5" />
                Add New Visi
              </button>
            </div>

            {/* Search Bar */}
            <div className="mb-8">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search visi content..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded-2xl py-4 pl-12 pr-6 focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-400 transition-all"
                />
                <div className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400">🔍</div>
              </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="px-8 py-5 text-left font-medium text-gray-600 w-20">ID</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600">DESCRIPTION</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600 w-44">CREATED AT</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600 w-44">UPDATED AT</th>
                    <th className="px-8 py-5 text-right font-medium text-gray-600 w-32">ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredVisiList.map((visi) => (
                    <tr key={visi.id} className="hover:bg-gray-50 transition-colors">
                      {/* ID Circle */}
                      <td className="px-8 py-6">
                        <div className="w-9 h-9 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold text-lg">
                          {visi.id}
                        </div>
                      </td>

                      {/* Description */}
                      <td className="px-8 py-6 text-gray-700 leading-relaxed">
                        {truncateDescription(visi.description)}
                      </td>

                      {/* Created At */}
                      <td className="px-8 py-6 text-gray-500">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {new Date(visi.created_at).toLocaleDateString('id-ID', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                      </td>

                      {/* Updated At */}
                      <td className="px-8 py-6 text-gray-500">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {new Date(visi.updated_at).toLocaleDateString('id-ID', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="px-8 py-6">
                        <div className="flex items-center justify-end gap-4">
                          <button
                            onClick={() => {
                              setEditingVisi(visi);
                              setIsModalOpen(true);
                            }}
                            className="p-3 text-blue-500 hover:bg-blue-50 rounded-2xl transition-all hover:scale-110"
                            title="Edit"
                          >
                            <Pencil className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteVisi(visi.id)}
                            className="p-3 text-red-500 hover:bg-red-50 rounded-2xl transition-all hover:scale-110"
                            title="Delete"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {filteredVisiList.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-20 text-center text-gray-400">
                        No visi found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Total Visi Footer */}
            <div className="bg-white rounded-3xl shadow-sm mt-6 p-6 text-center">
              <p className="text-gray-600 font-medium">
                Total Visi: <span className="text-gray-900">{totalVisi}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Add/Edit */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 w-full max-w-2xl">
            <h2 className="text-2xl font-semibold mb-6">
              {editingVisi ? "Edit Visi" : "Add New Visi"}
            </h2>
            <textarea
              value={editingVisi ? editingVisi.description : newVisi.description}
              onChange={(e) => {
                if (editingVisi) {
                  setEditingVisi({ ...editingVisi, description: e.target.value });
                } else {
                  setNewVisi({ description: e.target.value });
                }
              }}
              placeholder="Enter visi description..."
              className="w-full border border-gray-300 rounded-2xl px-6 py-4 h-48 resize-y focus:outline-none focus:border-purple-500 text-base"
            />
            <div className="flex gap-4 mt-8">
              <button
                onClick={() => setIsModalOpen(false)}
                className="flex-1 py-4 border border-gray-300 rounded-2xl font-medium hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={editingVisi ? handleUpdateVisi : handleCreateVisi}
                className="flex-1 py-4 bg-purple-600 hover:bg-purple-700 text-white rounded-2xl font-medium transition-all"
              >
                {editingVisi ? "Update Visi" : "Create Visi"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Notification */}
      {isSuccessModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/60 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center">
            <h2 className="text-2xl font-bold text-green-600 mb-4">Success</h2>
            <p className="text-gray-700 mb-6">{successMessage}</p>
            <button
              onClick={() => setIsSuccessModalOpen(false)}
              className="bg-green-500 text-white px-10 py-3 rounded-2xl hover:bg-green-600"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Error Notification */}
      {isErrorModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/60 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
            <p className="text-gray-700 mb-6">{error}</p>
            <button
              onClick={() => setIsErrorModalOpen(false)}
              className="bg-red-500 text-white px-10 py-3 rounded-2xl hover:bg-red-600"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisiManagement;